import axios from 'axios';
import { INTAKE_LINK_TOKEN_BY_BRIEF_PREFIX } from './mockData';

function unwrap(response) {
  return response && response.data ? response.data : response;
}

function requestSubmittedBriefsBadgeRefresh() {
  if (typeof window !== 'undefined' && typeof window.refreshSubmittedBriefsBadge === 'function') {
    window.refreshSubmittedBriefsBadge();
  }
}

function baseUrl(options = {}) {
  if (options.internal) {
    return '/briefs/api/briefs';
  }
  
  return '/api/briefs';
}

function groupsBaseUrl() {
  return '/briefs/api/brief-groups';
}

export async function createBrief(payload, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.post(baseUrl(options), payload, { headers });
  return unwrap(res);
}

export async function appendBriefReportChanges(id, payload, files = []) {
  const form = new FormData();
  form.append('content', payload.content);
  payload.report_queue_item_ids.forEach((itemId) => form.append('report_queue_item_ids[]', itemId));
  files.forEach((file) => form.append('files[]', file));

  const res = await axios.post(`/briefs/api/briefs/${encodeURIComponent(id)}/report-changes`, form);

  return unwrap(res);
}

export async function parseBriefPptx(file, options = {}) {
  const headers = options.token ? { token: options.token } : {};
  const form = new FormData();
  form.append('file', file);
  const res = await axios.post(`${baseUrl(options)}/slides/parse-pptx`, form, {
    headers,
    timeout: 180000,
  });
  return unwrap(res);
}

export async function createSlideFromImage(file, options = {}) {
  const headers = options.token ? { token: options.token } : {};
  const form = new FormData();
  form.append('file', file);
  const res = await axios.post(`${baseUrl(options)}/slides/from-image`, form, {
    headers,
    timeout: 180000,
    signal: options.signal,
    skipErrorToast: true,
  });
  return unwrap(res);
}

export async function listBriefs(params = {}, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.get(baseUrl(options), { headers, params });
  return unwrap(res);
}

export async function listBriefCreators(options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.get(`${baseUrl(options)}/creators`, { headers });
  return unwrap(res);
}

export async function getBrief(id, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const params = options.params || undefined;
  const res = await axios.get(`${baseUrl(options)}/${encodeURIComponent(id)}`, {
    headers,
    params,
    skipErrorToast: options.skipErrorToast === true,
  });
  return unwrap(res);
}

/**
 * Read-only brief payload for the shared brief panel (BriefContentPanel).
 *
 * Same shape a layout stores in `options.linked_brief`, so the Creative Studio
 * drawer renders exactly what the layout sidebar and proof page render.
 * Resolves to { ok, brief }.
 */
export async function getBriefPanel(id, options = {}) {
  const res = await axios.get(`${baseUrl(options)}/${encodeURIComponent(id)}/panel`, {
    skipErrorToast: options.skipErrorToast === true,
  });
  return unwrap(res);
}

export async function getSharedEditableBrief(token, params = {}) {
  const res = await axios.get(`/briefs/api/share/${encodeURIComponent(token)}/brief`, { params });
  return unwrap(res);
}

export async function updateSharedBrief(token, payload = {}, params = {}) {
  const res = await axios.patch(
    `/briefs/api/share/${encodeURIComponent(token)}/brief`,
    payload,
    { params },
  );
  return unwrap(res);
}

export async function uploadSharedBriefReferences(token, files, paths = [], options = {}) {
  return uploadReferenceBatches(files, paths, options, async (batchFiles, batchPaths, batchOptions) => {
    const form = referenceFormData(batchFiles, batchPaths, batchOptions.orderIndices);
    appendReferenceOptions(form, batchOptions);
    const res = await axios.post(
      `/briefs/api/share/${encodeURIComponent(token)}/brief/references`,
      form,
      { params: options.params || undefined, skipErrorToast: options.skipErrorToast === true },
    );
    return unwrap(res);
  });
}

export async function updateSharedBriefReferenceMarkup(token, referenceId, markup, params = {}) {
  const res = await axios.patch(
    `/briefs/api/share/${encodeURIComponent(token)}/brief/references/${encodeURIComponent(referenceId)}`,
    { markup },
    { params },
  );
  return unwrap(res);
}

export async function deleteSharedBriefReference(token, referenceId, params = {}) {
  const res = await axios.delete(
    `/briefs/api/share/${encodeURIComponent(token)}/brief/references/${encodeURIComponent(referenceId)}`,
    { params },
  );
  return unwrap(res);
}

export async function duplicateBrief(id, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.post(
    `${baseUrl(options)}/${encodeURIComponent(id)}/duplicate`,
    {},
    { headers },
  );
  return unwrap(res);
}

export async function listBriefTemplates(options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.get(`${baseUrl(options)}/templates`, { headers });
  return unwrap(res);
}

export async function createBlankBriefTemplate(options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.post(`${baseUrl(options)}/templates`, {}, { headers });
  return unwrap(res);
}

export async function saveBriefAsTemplate(id, name, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.post(
    `${baseUrl(options)}/${encodeURIComponent(id)}/save-as-template`,
    { name },
    { headers },
  );
  return unwrap(res);
}

export async function deleteBriefTemplate(templateId, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.delete(
    `${baseUrl(options)}/templates/${encodeURIComponent(templateId)}`,
    { headers },
  );
  return unwrap(res);
}

export async function startBriefTemplateEdit(templateId, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.post(
    `${baseUrl(options)}/templates/${encodeURIComponent(templateId)}/edit`,
    {},
    { headers },
  );
  return unwrap(res);
}

export async function duplicateBriefTemplate(templateId, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.post(
    `${baseUrl(options)}/templates/${encodeURIComponent(templateId)}/duplicate`,
    {},
    { headers },
  );
  return unwrap(res);
}

export async function updateBriefTemplate(templateId, payload = {}, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.put(
    `${baseUrl(options)}/templates/${encodeURIComponent(templateId)}`,
    payload,
    { headers },
  );
  return unwrap(res);
}

export async function createBriefFromTemplate(templateId, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.post(
    `${baseUrl(options)}/templates/${encodeURIComponent(templateId)}/create`,
    options.body && typeof options.body === 'object' ? options.body : {},
    { headers },
  );
  return unwrap(res);
}

export async function getBriefNotificationSettings(options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.get(`${baseUrl(options)}/notification-settings`, { headers });
  return unwrap(res);
}

export async function updateBriefNotificationSettings(emails, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.put(`${baseUrl(options)}/notification-settings`, { emails }, { headers });
  return unwrap(res);
}

export async function listAllowedSenders() {
  const res = await axios.get('/allowed-senders');
  return unwrap(res);
}

export async function addAllowedSender(domainOrEmail) {
  const res = await axios.post('/allowed-senders', { domain_or_email: domainOrEmail });
  return unwrap(res);
}

export async function removeAllowedSender(id) {
  const res = await axios.delete(`/allowed-senders/${encodeURIComponent(id)}`);
  return unwrap(res);
}

export async function listBriefGroups(options = {}) {
  const res = await axios.get(groupsBaseUrl(), {
    skipErrorToast: options.skipErrorToast === true,
  });
  return unwrap(res);
}

export async function createBriefGroup(name, options = {}) {
  const res = await axios.post(groupsBaseUrl(), { name }, {
    skipErrorToast: options.skipErrorToast === true,
  });
  return unwrap(res);
}

export async function updateBriefGroup(id, payload = {}, options = {}) {
  const res = await axios.patch(
    `${groupsBaseUrl()}/${encodeURIComponent(id)}`,
    payload,
    { skipErrorToast: options.skipErrorToast === true },
  );
  return unwrap(res);
}

export async function deleteBriefGroup(id, options = {}) {
  const res = await axios.delete(`${groupsBaseUrl()}/${encodeURIComponent(id)}`, {
    skipErrorToast: options.skipErrorToast === true,
  });
  return unwrap(res);
}

export async function updateBrief(id, payload, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.patch(`${baseUrl(options)}/${encodeURIComponent(id)}`, payload, { headers });
  if (payload && (payload.status !== undefined || payload.submitted)) {
    requestSubmittedBriefsBadgeRefresh();
  }
  return unwrap(res);
}

export async function deleteBrief(id, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.delete(`${baseUrl(options)}/${encodeURIComponent(id)}`, { headers });
  requestSubmittedBriefsBadgeRefresh();
  return unwrap(res);
}

export async function archiveBrief(id, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.post(
    `${baseUrl(options)}/${encodeURIComponent(id)}/archive`,
    {},
    { headers },
  );
  requestSubmittedBriefsBadgeRefresh();
  return unwrap(res);
}

export async function restoreBrief(id, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.post(
    `${baseUrl(options)}/${encodeURIComponent(id)}/restore`,
    {},
    { headers },
  );
  requestSubmittedBriefsBadgeRefresh();
  return unwrap(res);
}

export async function listBriefVersions(id, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.get(`${baseUrl(options)}/${encodeURIComponent(id)}/versions`, { headers });
  return unwrap(res);
}

export async function saveBriefVersion(id, payload = {}, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.post(`${baseUrl(options)}/${encodeURIComponent(id)}/versions`, payload, { headers });
  return unwrap(res);
}

export async function getBriefVersion(id, versionId, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.get(
    `${baseUrl(options)}/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}`,
    { headers },
  );
  return unwrap(res);
}

export async function restoreBriefVersion(id, versionId, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.post(
    `${baseUrl(options)}/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/restore`,
    {},
    { headers },
  );
  return unwrap(res);
}

export async function createBriefShare(id, payload = {}, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.post(`${baseUrl(options)}/${encodeURIComponent(id)}/shares`, payload, { headers });
  return unwrap(res);
}

export async function getBriefShare(token, params = {}, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const url = options.internal
    ? `/briefs/api/share/${encodeURIComponent(token)}`
    : `/api/briefs/share/${encodeURIComponent(token)}`;
  const res = await axios.get(url, { headers, params });
  return unwrap(res);
}

export async function notifyBriefShareVisitor(token, payload = {}, params = {}) {
  const res = await axios.post(
    `/briefs/api/share/${encodeURIComponent(token)}/visitor`,
    payload,
    { params },
  );
  return unwrap(res);
}

export async function createBriefShareComment(token, payload = {}, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const url = options.internal
    ? `/briefs/api/share/${encodeURIComponent(token)}/comments`
    : `/api/briefs/share/${encodeURIComponent(token)}/comments`;
  const res = await axios.post(url, payload, { headers });
  return unwrap(res);
}

function parseDownloadFilename(contentDisposition, fallback) {
  if (!contentDisposition) {
    return fallback;
  }
  const match = /filename\*?=(?:UTF-8''|")?([^";\n]+)/i.exec(contentDisposition);
  if (match && match[1]) {
    return decodeURIComponent(match[1].replace(/"/g, '').trim());
  }
  return fallback;
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export async function downloadSharedBriefExport(token, format, params = {}) {
  const ext = format === 'pptx' ? 'pptx' : 'pdf';
  const res = await axios.get(
    `/briefs/api/share/${encodeURIComponent(token)}/export/${ext}`,
    { params, responseType: 'blob' },
  );
  saveExportBlob(res, ext);
}

export async function downloadBriefExport(id, format, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const ext = format === 'pptx' ? 'pptx' : 'pdf';
  const res = await axios.get(
    `${baseUrl(options)}/${encodeURIComponent(id)}/export/${ext}`,
    { headers, responseType: 'blob' },
  );
  saveExportBlob(res, ext);
}

function saveExportBlob(res, ext) {
  const mime = ext === 'pptx'
    ? 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    : 'application/pdf';
  const blob = new Blob([res.data], { type: res.headers['content-type'] || mime });
  const filename = parseDownloadFilename(
    res.headers['content-disposition'],
    `brief.${ext}`,
  );
  triggerBlobDownload(blob, filename);
}

export async function exportBriefJson(id, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.get(
    `${baseUrl(options)}/${encodeURIComponent(id)}/export/json`,
    { headers, responseType: 'blob' },
  );
  const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/json' });
  const filename = parseDownloadFilename(res.headers['content-disposition'], 'brief-export.json');
  triggerBlobDownload(blob, filename);
}

/** Resolves to { ok, brief, warnings } — warnings lists any reference/asset that could not be re-uploaded. */
export async function importBriefJson(file, options = {}) {
  const headers = options.token ? { token: options.token } : {};
  const form = new FormData();
  form.append('file', file);
  const res = await axios.post(`${baseUrl(options)}/import`, form, { headers });
  return unwrap(res);
}

export async function emailBrief(id, payload, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.post(
    `${baseUrl(options)}/${encodeURIComponent(id)}/email`,
    payload,
    { headers },
  );
  return unwrap(res);
}

export async function handoffBriefToCreativeStudio(id, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.get(
    `${baseUrl(options)}/${encodeURIComponent(id)}/handoff/creative-studio`,
    { headers },
  );
  return unwrap(res);
}

const INTAKE_API_BASE = '/briefs/api/intake';
const INTAKE_TOKEN_STORAGE_PREFIX = 'brief-intake-token:';

export function getStoredIntakeToken(briefId) {
  if (typeof window === 'undefined') {
    return '';
  }
  try {
    const fromStorage = sessionStorage.getItem(`${INTAKE_TOKEN_STORAGE_PREFIX}${briefId}`);
    if (fromStorage) {
      return fromStorage;
    }
  } catch {
    /* ignore */
  }
  const fromQuery = new URLSearchParams(window.location.search).get('intake_token');
  return fromQuery && fromQuery.trim() ? fromQuery.trim() : '';
}

export function persistIntakeToken(briefId, token) {
  if (!briefId || !token || typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(`${INTAKE_TOKEN_STORAGE_PREFIX}${briefId}`, token);
  } catch {
    /* ignore */
  }
}

const INTAKE_RETURN_URL_PREFIX = 'brief-intake-return:';

export function persistIntakeReturnUrl(briefId, url) {
  if (!briefId || !url || typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(`${INTAKE_RETURN_URL_PREFIX}${briefId}`, url);
  } catch {
    /* ignore */
  }
}

export function getIntakeReturnUrl(briefId) {
  if (!briefId || typeof window === 'undefined') {
    return '';
  }
  try {
    return sessionStorage.getItem(`${INTAKE_RETURN_URL_PREFIX}${briefId}`) || '';
  } catch {
    return '';
  }
}

export function getStoredIntakeLinkToken(briefId) {
  if (!briefId || typeof window === 'undefined') {
    return '';
  }
  try {
    return sessionStorage.getItem(`${INTAKE_LINK_TOKEN_BY_BRIEF_PREFIX}${briefId}`) || '';
  } catch {
    return '';
  }
}

export function persistIntakeLinkToken(briefId, token) {
  if (!briefId || !token || typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(`${INTAKE_LINK_TOKEN_BY_BRIEF_PREFIX}${briefId}`, token);
  } catch {
    /* ignore */
  }
}

function intakeAuthHeaders(briefId) {
  const headers = {};
  const token = getStoredIntakeToken(briefId);
  if (token) {
    headers['X-Brief-Intake-Token'] = token;
  }
  const linkToken = getStoredIntakeLinkToken(briefId);
  if (linkToken) {
    headers['X-Brief-Intake-Link'] = linkToken;
  }
  return headers;
}

export async function getIntakeOrganization(slug) {
  const res = await axios.get(`${INTAKE_API_BASE}/${encodeURIComponent(slug)}`);
  return unwrap(res);
}

export async function requestIntakeLink(slug, payload) {
  const res = await axios.post(
    `${INTAKE_API_BASE}/${encodeURIComponent(slug)}/request-link`,
    payload,
  );
  return unwrap(res);
}

export async function redeemIntakeLink(token) {
  const res = await axios.get(
    `${INTAKE_API_BASE}/link/${encodeURIComponent(token)}`,
  );
  return unwrap(res);
}

export async function extractIntakeDescriptionDocument(file) {
  const form = new FormData();
  form.append('file', file);
  const res = await axios.post(`${INTAKE_API_BASE}/extract-description`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  return unwrap(res);
}

export async function createIntakeBrief(slug, payload) {
  const res = await axios.post(
    `${INTAKE_API_BASE}/${encodeURIComponent(slug)}/briefs`,
    payload,
  );
  return unwrap(res);
}

export async function listIntakeTemplates(slug) {
  const res = await axios.get(`${INTAKE_API_BASE}/${encodeURIComponent(slug)}/templates`);
  return unwrap(res);
}

export async function getIntakeBrief(id) {
  const res = await axios.get(
    `${INTAKE_API_BASE}/briefs/${encodeURIComponent(id)}`,
    { headers: intakeAuthHeaders(id) },
  );
  return unwrap(res);
}

export async function updateIntakeBrief(id, payload) {
  const res = await axios.patch(
    `${INTAKE_API_BASE}/briefs/${encodeURIComponent(id)}`,
    payload,
    { headers: intakeAuthHeaders(id) },
  );
  return unwrap(res);
}

function referenceFormData(files, paths = [], orderIndices = []) {
  const form = new FormData();
  (files || []).forEach((file, index) => {
    form.append('files[]', file);
    const relativePath = paths[index]
      || file.webkitRelativePath
      || file.name
      || '';
    if (relativePath) {
      form.append('paths[]', relativePath);
    }
    if (orderIndices.length) {
      form.append('order_indices[]', orderIndices[index] == null ? '' : String(orderIndices[index]));
    }
  });
  return form;
}

function appendReferenceOptions(form, options) {
  if (options.slotIndex != null) {
    form.append('slot_index', String(options.slotIndex));
  }
  if (options.sectionType) {
    form.append('section_type', String(options.sectionType));
  }
  if (options.inline) {
    form.append('inline', '1');
  }
  (options.inlinePlaceholders || []).forEach((placeholder) => {
    form.append('inline_placeholders[]', placeholder);
  });
}

// Keep each multipart request under k8 post_max_size (100M) and PHP max_file_uploads.
const UPLOAD_BATCH_MAX_FILES = 5;
const UPLOAD_BATCH_MAX_BYTES = 40 * 1024 * 1024;

function chunkFilesForUpload(files, paths = [], placeholders = [], orderIndices = []) {
  const batches = [];
  let currentFiles = [];
  let currentPaths = [];
  let currentPlaceholders = [];
  let currentOrderIndices = [];
  let currentBytes = 0;

  (files || []).forEach((file, index) => {
    const size = file && typeof file.size === 'number' ? file.size : 0;
    const wouldExceed = currentFiles.length > 0
      && (currentFiles.length >= UPLOAD_BATCH_MAX_FILES || currentBytes + size > UPLOAD_BATCH_MAX_BYTES);
    if (wouldExceed) {
      batches.push({
        files: currentFiles,
        paths: currentPaths,
        placeholders: currentPlaceholders,
        orderIndices: currentOrderIndices,
      });
      currentFiles = [];
      currentPaths = [];
      currentPlaceholders = [];
      currentOrderIndices = [];
      currentBytes = 0;
    }
    currentFiles.push(file);
    currentPaths.push(paths[index] || '');
    currentPlaceholders.push(placeholders[index] || null);
    currentOrderIndices.push(orderIndices[index] ?? null);
    currentBytes += size;
  });

  if (currentFiles.length) {
    batches.push({
      files: currentFiles,
      paths: currentPaths,
      placeholders: currentPlaceholders,
      orderIndices: currentOrderIndices,
    });
  }

  return batches;
}

async function uploadReferenceBatches(files, paths, options, postBatch) {
  const placeholders = options.inlinePlaceholders || [];
  const batches = chunkFilesForUpload(files, paths, placeholders, options.orderIndices || []);
  let lastResult = null;
  for (const batch of batches) {
    const batchOptions = {
      ...options,
      inlinePlaceholders: options.inline
        ? batch.placeholders
        : options.inlinePlaceholders,
      orderIndices: options.orderIndices ? batch.orderIndices : [],
    };
    lastResult = await postBatch(batch.files, batch.paths, batchOptions);
  }
  return lastResult;
}

export async function linkBriefReferences(id, userFileIds, options = {}) {
  const headers = options.token ? { token: options.token } : {};
  const body = { user_file_ids: userFileIds };
  if (options.slotIndex != null) {
    body.slot_index = options.slotIndex;
  }
  if (options.sectionType) {
    body.section_type = options.sectionType;
  }
  const res = await axios.post(
    `${baseUrl(options)}/${encodeURIComponent(id)}/references/link`,
    body,
    { headers },
  );
  return unwrap(res);
}

export async function importBriefReferences(id, assets, options = {}) {
  const headers = options.token ? { token: options.token } : {};
  const body = { assets };
  if (options.slotIndex != null) {
    body.slot_index = options.slotIndex;
  }
  if (options.sectionType) {
    body.section_type = options.sectionType;
  }
  const res = await axios.post(
    `${baseUrl(options)}/${encodeURIComponent(id)}/references/import`,
    body,
    { headers },
  );
  return unwrap(res);
}

/**
 * Every image on an Amazon product page, from an ASIN or a product URL — the same
 * lookup Creative Studio uses for "Match a style". Returns [] when nothing is found.
 * (POR-14794)
 */
export async function searchAmazonProductImages(asinOrUrl) {
  const res = await axios.post('/networks/search-product-images', {
    product_references: [{ type: 'asin', value: asinOrUrl }],
    multi: true,
  }, { skipErrorToast: true });
  const results = unwrap(res)?.results || [];
  const hit = results.find((r) => r && r.found && Array.isArray(r.images) && r.images.length);
  return hit ? hit.images.filter((url) => typeof url === 'string' && url) : [];
}

export async function uploadBriefReferences(id, files, options = {}) {
  return uploadReferenceBatches(files, options.paths || [], options, async (batchFiles, batchPaths, batchOptions) => {
    const headers = options.token ? { token: options.token } : {};
    const form = referenceFormData(batchFiles, batchPaths, batchOptions.orderIndices);
    appendReferenceOptions(form, batchOptions);
    const res = await axios.post(
      `${baseUrl(options)}/${encodeURIComponent(id)}/references`,
      form,
      { headers, skipErrorToast: options.skipErrorToast === true },
    );
    return unwrap(res);
  });
}

export async function uploadIntakeBriefReferences(id, files, paths = [], options = {}) {
  return uploadReferenceBatches(files, paths, options, async (batchFiles, batchPaths, batchOptions) => {
    const form = referenceFormData(batchFiles, batchPaths, batchOptions.orderIndices);
    appendReferenceOptions(form, batchOptions);
    const res = await axios.post(
      `${INTAKE_API_BASE}/briefs/${encodeURIComponent(id)}/references`,
      form,
      { headers: intakeAuthHeaders(id), skipErrorToast: options.skipErrorToast === true },
    );
    return unwrap(res);
  });
}

export async function updateBriefReferenceMarkup(id, referenceId, markup, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.patch(
    `${baseUrl(options)}/${encodeURIComponent(id)}/references/${encodeURIComponent(referenceId)}`,
    { markup },
    { headers },
  );
  return unwrap(res);
}

export async function updateIntakeBriefReferenceMarkup(id, referenceId, markup) {
  const res = await axios.patch(
    `${INTAKE_API_BASE}/briefs/${encodeURIComponent(id)}/references/${encodeURIComponent(referenceId)}`,
    { markup },
    { headers: intakeAuthHeaders(id) },
  );
  return unwrap(res);
}

export async function deleteBriefReference(id, referenceId, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.delete(
    `${baseUrl(options)}/${encodeURIComponent(id)}/references/${encodeURIComponent(referenceId)}`,
    { headers },
  );
  return unwrap(res);
}

export async function deleteIntakeBriefReference(id, referenceId) {
  const res = await axios.delete(
    `${INTAKE_API_BASE}/briefs/${encodeURIComponent(id)}/references/${encodeURIComponent(referenceId)}`,
    { headers: intakeAuthHeaders(id) },
  );
  return unwrap(res);
}

export async function uploadPendingAttachments(briefId, pending, options = {}) {
  const items = (pending || []).filter((item) => item && item.file instanceof File);
  const inlineItems = items.filter((item) => item.inline);
  const chipItems = items.filter((item) => !item.inline);
  const chipResult = await uploadAttachmentItems(briefId, chipItems, options);
  const inlineResult = await uploadAttachmentItems(briefId, inlineItems, {
    ...options,
    inline: true,
    sectionType: options.sectionType || 'objective',
  });
  return inlineResult || chipResult;
}

async function uploadAttachmentItems(briefId, items, options) {
  if (!items.length) {
    return null;
  }
  const files = items.map((item) => item.file);
  const paths = items.map((item) => item.relativePath || item.file.webkitRelativePath || item.file.name || '');
  const batchOptions = { ...options };
  if (options.inline) {
    batchOptions.inlinePlaceholders = items.map((item) => item.id);
  }
  if (options.intake) {
    return uploadIntakeBriefReferences(briefId, files, paths, batchOptions);
  }
  return uploadBriefReferences(briefId, files, { ...batchOptions, paths });
}

export async function listBriefRequestInvites() {
  const res = await axios.get('/briefs/api/request-invites');
  return unwrap(res);
}

export async function createBriefRequestInvite(payload) {
  const res = await axios.post('/briefs/api/request-invites', payload);
  return unwrap(res);
}

export async function joinSharedBriefRequest(token, payload) {
  const res = await axios.post(
    `/briefs/api/shared-request/${encodeURIComponent(token)}/join`,
    payload,
  );
  return unwrap(res);
}

export async function revokeBriefRequestInvite(id) {
  const res = await axios.delete(`/briefs/api/request-invites/${encodeURIComponent(id)}`);
  return unwrap(res);
}

function workroomBaseUrl(briefId, options = {}) {
  return `${baseUrl(options)}/${encodeURIComponent(briefId)}/workroom`;
}

export async function getBriefWorkroom(briefId, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.get(workroomBaseUrl(briefId, options), {
    headers,
    skipErrorToast: options.skipErrorToast === true,
  });
  return unwrap(res);
}

export async function updateBriefWorkroom(briefId, payload, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.patch(workroomBaseUrl(briefId, options), payload, { headers });
  if (payload && payload.assigned_to !== undefined) {
    requestSubmittedBriefsBadgeRefresh();
  }
  return unwrap(res);
}

export async function appendBriefWorkroomLayout(briefId, layout, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.post(
    `${workroomBaseUrl(briefId, options)}/layouts`,
    layout,
    { headers },
  );
  return unwrap(res);
}

/**
 * Rebuild the layout's linked-brief snapshot from the live brief, without
 * storing it — for refreshing the sidebar against a brief edited since it was
 * linked. Resolves to { linked_brief }, null when nothing is linked or the
 * brief is gone.
 */
export async function getLayoutLinkedBrief(customerId, layoutId) {
  const res = await axios.get(
    `/banner/${encodeURIComponent(customerId)}/group/${encodeURIComponent(layoutId)}/link_brief`,
  );
  return unwrap(res);
}

/**
 * Link a brief to a layout, or unlink it by passing a falsy briefId.
 *
 * Only the id travels — the server builds the snapshot that gets stored in the
 * layout's options, so the layout sidebar and Creative Studio can't drift apart.
 * Resolves to { ok, linked_brief } where linked_brief is null once unlinked.
 */
export async function linkLayoutToBrief(customerId, layoutId, briefId) {
  const res = await axios.post(
    `/banner/${encodeURIComponent(customerId)}/group/${encodeURIComponent(layoutId)}/link_brief`,
    { brief_id: briefId || null },
  );
  return unwrap(res);
}

export async function createBriefWorkroomComment(briefId, payload, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const formData = payload instanceof FormData ? payload : (() => {
    const fd = new FormData();
    if (payload.body) fd.append('body', payload.body);
    if (payload.file) fd.append('file', payload.file);
    return fd;
  })();
  const res = await axios.post(
    `${workroomBaseUrl(briefId, options)}/comments`,
    formData,
    { headers },
  );
  return unwrap(res);
}

export async function createBriefWorkroomDelivery(briefId, formData, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.post(
    `${workroomBaseUrl(briefId, options)}/deliveries`,
    formData,
    { headers },
  );
  return unwrap(res);
}

export async function updateBriefWorkroomComment(briefId, commentId, payload, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.patch(
    `${workroomBaseUrl(briefId, options)}/comments/${encodeURIComponent(commentId)}`,
    payload,
    { headers },
  );
  return unwrap(res);
}

export async function deleteBriefWorkroomComment(briefId, commentId, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.delete(
    `${workroomBaseUrl(briefId, options)}/comments/${encodeURIComponent(commentId)}`,
    { headers },
  );
  return unwrap(res);
}

export async function uploadBriefWorkroomFile(briefId, file, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const formData = new FormData();
  formData.append('file', file);
  const res = await axios.post(
    `${workroomBaseUrl(briefId, options)}/files`,
    formData,
    { headers },
  );
  return unwrap(res);
}

export async function deleteBriefWorkroomFile(briefId, fileId, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.delete(
    `${workroomBaseUrl(briefId, options)}/files/${encodeURIComponent(fileId)}`,
    { headers },
  );
  return unwrap(res);
}

async function searchWorkroomItems(briefId, type, query = '', options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const searchQuery = typeof query === 'string' ? query.trim() : '';
  const res = await axios.get(`${workroomBaseUrl(briefId, options)}/search`, {
    headers,
    skipErrorToast: options.skipErrorToast === true,
    params: {
      type,
      q: searchQuery || undefined,
    },
  });
  const items = unwrap(res)?.items || [];

  return items.map((item) => ({
    key: `${type}-${item.id}`,
    ...item,
  }));
}

export async function searchWorkroomCompanyUsers(briefId, query = '', options = {}) {
  return searchWorkroomItems(briefId, 'users', query, options);
}

export async function searchWorkroomLayouts(briefId, query = '', options = {}) {
  return searchWorkroomItems(briefId, 'layouts', query, options);
}

export async function searchWorkroomProofs(briefId, query = '', options = {}) {
  return searchWorkroomItems(briefId, 'proofs', query, options);
}

function linksBaseUrl(briefId, options = {}) {
  return `${baseUrl(options)}/${encodeURIComponent(briefId)}/links`;
}

export async function getBriefLinks(briefId, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.get(linksBaseUrl(briefId, options), {
    headers,
    skipErrorToast: options.skipErrorToast === true,
  });
  return unwrap(res);
}

export async function searchBriefLinkTargets(briefId, kind, query = '', options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const searchQuery = typeof query === 'string' ? query.trim() : '';
  const res = await axios.get(`${linksBaseUrl(briefId, options)}/search`, {
    headers,
    params: {
      kind,
      q: searchQuery || undefined,
    },
  });
  const items = unwrap(res)?.items || [];
  return items.map((item) => ({
    key: `${kind}-${item.id}`,
    ...item,
  }));
}

export async function attachBriefLink(briefId, payload, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.post(linksBaseUrl(briefId, options), payload, { headers });
  return unwrap(res);
}

export async function detachBriefLink(briefId, kind, targetId, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.delete(
    `${linksBaseUrl(briefId, options)}/${encodeURIComponent(kind)}/${encodeURIComponent(targetId)}`,
    { headers },
  );
  return unwrap(res);
}

export async function updateBriefTrackerSettings(briefId, payload, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const body = typeof payload === 'string' ? { name_column: payload } : payload;
  const res = await axios.patch(
    `${baseUrl(options)}/${encodeURIComponent(briefId)}/tracker`,
    body,
    { headers },
  );
  return unwrap(res);
}

export async function addBriefTrackerRow(briefId, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.post(
    `${baseUrl(options)}/${encodeURIComponent(briefId)}/tracker/rows`,
    {},
    { headers },
  );
  return unwrap(res);
}

export async function updateBriefTrackerRow(briefId, rowId, payload, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const body = typeof payload === 'string' ? { status: payload } : payload;
  const res = await axios.patch(
    `${baseUrl(options)}/${encodeURIComponent(briefId)}/tracker/rows/${encodeURIComponent(rowId)}`,
    body,
    { headers, skipErrorToast: options.skipErrorToast === true },
  );
  return unwrap(res);
}

export async function deleteBriefTrackerRow(briefId, rowId, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.delete(
    `${baseUrl(options)}/${encodeURIComponent(briefId)}/tracker/rows/${encodeURIComponent(rowId)}`,
    { headers },
  );
  return unwrap(res);
}

export async function shareBriefTracker(briefId, nameColumn, options = {}) {
  const headers = options.token ? { token: options.token } : undefined;
  const res = await axios.post(
    `${baseUrl(options)}/${encodeURIComponent(briefId)}/tracker/share`,
    { name_column: nameColumn },
    { headers },
  );
  return unwrap(res);
}

export async function getPublicBriefTracker(token) {
  const res = await axios.get(`/briefs/api/tracker/${encodeURIComponent(token)}`);
  return unwrap(res);
}

export async function updatePublicBriefTrackerRow(token, rowId, status) {
  const res = await axios.patch(
    `/briefs/api/tracker/${encodeURIComponent(token)}/rows/${encodeURIComponent(rowId)}`,
    { status },
  );
  return unwrap(res);
}
